/**
 * React Query hooks for the standalone-onboarding endpoints (CLA-520).
 *
 * Backend: lib/claro_web/controllers/api/v1/onboarding_controller.ex
 *
 * The flow this powers:
 *   1. confirm_practitioner_identity — sets practitioner_type / ahpra_number /
 *      qualifications on the user record.
 *   2. create_first_clinic — atomic org + clinic + branding + ownership.
 *   3. add_additional_clinic — same- or separate-business branch for owners
 *      who have more than one clinic.
 *   4. extract_ahpra / extract_logo_colour — multipart vision endpoints
 *      that turn capture moments into confirmation moments.
 *   5. abr_search / abr_verify — Australian Business Register lookups.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './client'
import { useAuthStore } from '@/stores/auth'

export type PractitionerType = 'registered' | 'non_registered' | 'admin_only'

export interface PractitionerIdentityInput {
  practitioner_type: PractitionerType
  ahpra_number?: string
  qualifications?: string
  first_name?: string
  last_name?: string
  provider_number?: string
  prescriber_number?: string
  // CLA-886 — US capture. `npi` persists to users.npi; the license_* fields
  // ride along so the backend's dual-write can shape the US registry row.
  npi?: string
  license_state?: string
  license_type?: string
  license_issuer?: string
}

export interface CreateClinicInput {
  display_name: string
  primary_color?: string
  address?: string
  suburb?: string
  state?: string
  postcode?: string
  timezone?: string
  abn?: string
  legal_name?: string | null
  trading_name?: string | null
  practises_clinically?: boolean
}

export interface AdditionalClinicInput extends CreateClinicInput {
  shared_business: boolean
  /**
   * Frontend-friendly path for "same business": pass an existing clinic the
   * user is a member of, and the server resolves the org. Avoids leaking
   * the org abstraction into the UI.
   */
  shared_with_clinic_id?: number
  /** Advanced alternative to shared_with_clinic_id; rarely needed. */
  org_id?: number
}

export interface ClinicCreated {
  org_id: number
  clinic_id: number
  clinic_users_id: number | null
}

export interface AhpraExtraction {
  ahpra_number: string | null
  first_name: string | null
  last_name: string | null
  profession: string | null
  registration_status: string | null
  expires_at: string | null
  confidence: 'high' | 'medium' | 'low'
}

export interface LogoColour {
  primary_color: string
  confidence: 'high' | 'medium' | 'low'
}

export interface AbrMatch {
  abn: string
  legal_name: string | null
  trading_name: string | null
  state: string | null
  postcode: string | null
  status: string | null
  gst_registered: boolean
}

// --- Hooks -----------------------------------------------------------------

export function useConfirmPractitionerIdentity() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: PractitionerIdentityInput) =>
      api.post<{ status: string }>('/onboarding/practitioner_identity', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings'] })
    },
  })
}

export function useCreateClinic() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateClinicInput) =>
      api.post<ClinicCreated>('/onboarding/clinics', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings'] })
      // Refresh the Zustand auth store so the next wizard step (e.g.
      // AnotherClinic reading clinics[0] for the same-business branch)
      // sees the clinic just created. Without this the store keeps the
      // empty list AppShell loaded with at first-launch — Tara hit this
      // in prod, the second-clinic submit kept 400-ing with
      // share_target_required because clinics[0] was undefined.
      void useAuthStore.getState().init()
    },
  })
}

export function useAddAdditionalClinic() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: AdditionalClinicInput) =>
      api.post<ClinicCreated>('/onboarding/clinics/additional', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings'] })
      void useAuthStore.getState().init()
    },
  })
}

export function useExtractAhpra() {
  return useMutation({
    mutationFn: async (file: File): Promise<AhpraExtraction> => {
      const fd = new FormData()
      fd.append('image', file)
      return api.postForm<AhpraExtraction>('/onboarding/extract/ahpra', fd)
    },
  })
}

export function useExtractLogoColour() {
  return useMutation({
    mutationFn: async (file: File): Promise<LogoColour> => {
      const fd = new FormData()
      fd.append('image', file)
      return api.postForm<LogoColour>('/onboarding/extract/logo_colour', fd)
    },
  })
}

/**
 * Search ABR by business name. Returns up to 10 candidates, or `null` when
 * the ABR lookup is not configured (frontend should hide the search UI and
 * fall back to manual ABN entry).
 */
export async function searchAbr(name: string): Promise<AbrMatch[] | null> {
  try {
    const resp = await api.get<{ matches: AbrMatch[] }>(
      `/onboarding/abr/search?q=${encodeURIComponent(name)}`,
    )
    return resp.matches
  } catch (err) {
    if (err instanceof Error && 'status' in err && (err as { status: number }).status === 503) {
      return null
    }
    throw err
  }
}

export async function verifyAbn(abn: string): Promise<AbrMatch[] | null> {
  try {
    const resp = await api.get<{ matches: AbrMatch[] }>(
      `/onboarding/abr/verify/${encodeURIComponent(abn)}`,
    )
    return resp.matches
  } catch (err) {
    if (err instanceof Error && 'status' in err && (err as { status: number }).status === 503) {
      return null
    }
    throw err
  }
}

// --- Document drop ---------------------------------------------------------

export interface ClinicDocument {
  id: number
  kind: string
  original_filename: string | null
  content_type: string | null
  file_size_bytes: number | null
  processing_status: 'pending' | 'processing' | 'completed' | 'failed' | 'skipped'
  uploaded_at: string
}

export function useUploadClinicDocument(clinicId: number) {
  return useMutation({
    mutationFn: async ({ file, kind }: { file: File; kind?: string }) => {
      const fd = new FormData()
      fd.append('file', file)
      if (kind) fd.append('kind', kind)
      return api.postForm<ClinicDocument>(
        `/onboarding/clinics/${clinicId}/documents`,
        fd,
      )
    },
  })
}

// --- Complete your clinic profile (CLA-711) --------------------------------

/**
 * Clinic-profile completion state for the active clinic. Per ADR-013
 * (capability gating by documentation), `complete` is an identity nudge only —
 * it tracks whether the clinic is still an unconfigured placeholder, never the
 * documents under `unlocks` (those gate capabilities, not core use). `prefill`
 * carries everything we already hold so the flow never makes the owner re-type.
 */
export interface ProfileStatus {
  clinic_id: number | null
  clinic_name: string | null
  complete: boolean
  // `ein` / `npi` are US fields (CLA-1842); optional so an older server that
  // doesn't send them still type-checks and renders.
  needs: { rename: boolean; abn: boolean; ein?: boolean; address: boolean }
  /** `ahpra` = registration missing: AHPRA in AU, the state license in the US. */
  unlocks: { indemnity: boolean; ahpra: boolean; npi?: boolean }
  prefill: {
    name: string | null
    abn: string | null
    ein?: string | null
    legal_name: string | null
    trading_name: string | null
    address: string | null
    suburb: string | null
    state: string | null
    postcode: string | null
    ahpra_number: string | null
    npi?: string | null
  }
}

export interface UpdateClinicProfileInput {
  clinicId: number
  name?: string
  abn?: string
  /** US practice's EIN, 9 digits; the server stores it as 12-3456789. */
  ein?: string
  legal_name?: string | null
  trading_name?: string | null
  address?: string
  suburb?: string
  state?: string
  postcode?: string
}

export function useProfileStatus() {
  return useQuery({
    queryKey: ['profile-status'],
    queryFn: () => api.get<ProfileStatus>('/onboarding/profile-status'),
    // Per-session nudge — no need to refetch aggressively. The mutation below
    // invalidates this key when the owner makes progress.
    staleTime: 5 * 60 * 1000,
  })
}

export function useUpdateClinicProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ clinicId, ...body }: UpdateClinicProfileInput) =>
      api.put<{ status: string }>(`/onboarding/clinics/${clinicId}`, body),
    onSuccess: () => {
      // Two caches reflect clinic identity: the profile-status query (the
      // banner) and the Zustand auth store (clinic name in the shell). Refresh
      // both — the auth store re-pulls /auth/me. Mirrors the Tara-bug fix above.
      qc.invalidateQueries({ queryKey: ['profile-status'] })
      qc.invalidateQueries({ queryKey: ['settings'] })
      void useAuthStore.getState().init()
    },
  })
}
